import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="msa-viewer"
export default class extends Controller {
  static targets = ["container", "ruler", "sequences", "startPos", "endPos", "length", "blockSizeSelect"]
  static values = {
    sequences: Array,
    headers: Array,
    maxLength: Number
  }

  connect() {
    console.log("MSA Viewer connected")
    this.currentStart = 1
    this.blockSize = 100
    this.currentEnd = Math.min(this.blockSize, this.maxLengthValue)

    this.render()
    this.addNucleotideColors()
  }

  render() {
    this.renderRuler()
    this.renderSequences()
    this.updateControls()
  }

  renderRuler() {
    if (!this.hasRulerTarget) return

    let ruler = ""

    // Create a character-by-character ruler where each position takes exactly 1 character
    for (let i = this.currentStart; i <= this.currentEnd; i++) {
      if (i % 10 === 1) {
        // Show first digit of position at 1, 11, 21, 31, etc.
        const posStr = i.toString()
        const firstDigit = posStr.length > 1 ? posStr[posStr.length - 2] : ' '
        ruler += `<span class="position-marker" title="Position ${i}">${firstDigit}</span>`
      } else if (i % 10 === 0) {
        // Show last digit (0) at positions 10, 20, 30, etc.
        ruler += `<span class="position-marker" title="Position ${i}">0</span>`
      } else if (i % 5 === 0) {
        // Show colon at positions 5, 15, 25, etc.
        ruler += `<span class="tick-mark">:</span>`
      } else {
        // Show space for other positions - wrap space in span for consistent width
        ruler += `<span class="tick-dot">·</span>`
      }
    }

    this.rulerTarget.innerHTML = ruler
  }

  renderSequences() {
    if (!this.hasSequencesTarget) return

    const sequencesHtml = this.sequencesValue.map((sequence, index) => {
      const header = this.headersValue[index] || `Sequence ${index + 1}`
      const truncatedHeader = this.truncateHeader(header)
      const subsequence = sequence.substring(this.currentStart - 1, this.currentEnd)

      return `
        <div class="msa-row">
          <div class="msa-header" title="${this.escapeHtml(header)}">${this.escapeHtml(truncatedHeader)}</div>
          <div class="msa-sequence" data-sequence-index="${index}">${this.escapeHtml(subsequence)}</div>
        </div>
      `
    }).join('')

    this.sequencesTarget.innerHTML = sequencesHtml
  }

  truncateHeader(header, maxLength = 30) {
    if (header.length <= maxLength) return header
    return header.substring(0, maxLength - 3) + "..."
  }

  updateControls() {
    // Update position display
    if (this.hasStartPosTarget) {
      const startPosDisplays = this.startPosTargets
      startPosDisplays.forEach(el => {
        if (el.tagName === 'INPUT') {
          el.value = this.currentStart
        } else {
          el.textContent = this.currentStart
        }
      })
    }
    if (this.hasEndPosTarget) {
      this.endPosTarget.textContent = this.currentEnd
    }
    if (this.hasLengthTarget) {
      this.lengthTarget.textContent = this.maxLengthValue
    }
  }

  addNucleotideColors() {
    const sequences = this.element.querySelectorAll('.msa-sequence')
    sequences.forEach(sequence => {
      this.colorizeSequence(sequence)
    })
  }

  colorizeSequence(element) {
    const text = element.textContent
    let coloredText = ''

    // Wrap each character in a span with fixed width
    for (let i = 0; i < text.length; i++) {
      const nucleotide = text[i].toUpperCase()
      const className = this.getNucleotideClass(nucleotide)
      coloredText += `<span class="${className}">${text[i]}</span>`
    }

    element.innerHTML = coloredText
  }

  getNucleotideClass(nucleotide) {
    const classMap = {
      'A': 'nucleotide-a',
      'T': 'nucleotide-t',
      'G': 'nucleotide-g',
      'C': 'nucleotide-c',
      'U': 'nucleotide-u',
      'N': 'nucleotide-n',
      '-': 'nucleotide-gap'
    }
    return classMap[nucleotide] || 'nucleotide-unknown'
  }

  // Navigation methods
  goToStart() {
    this.currentStart = 1
    this.currentEnd = Math.min(this.blockSize, this.maxLengthValue)
    this.render()
    this.addNucleotideColors()
  }

  goToEnd() {
    this.currentEnd = this.maxLengthValue
    this.currentStart = Math.max(1, this.maxLengthValue - this.blockSize + 1)
    this.render()
    this.addNucleotideColors()
  }

  previous() {
    if (this.currentStart > 1) {
      const shift = Math.min(this.blockSize, this.currentStart - 1)
      this.currentStart -= shift
      this.currentEnd -= shift
      this.render()
      this.addNucleotideColors()
    }
  }

  next() {
    if (this.currentEnd < this.maxLengthValue) {
      const shift = Math.min(this.blockSize, this.maxLengthValue - this.currentEnd)
      this.currentStart += shift
      this.currentEnd += shift
      this.render()
      this.addNucleotideColors()
    }
  }

  goToPosition() {
    const inputField = this.startPosTargets.find(el => el.tagName === 'INPUT')
    if (!inputField) return

    const start = parseInt(inputField.value)
    if (start >= 1 && start <= this.maxLengthValue) {
      this.currentStart = start
      this.currentEnd = Math.min(start + this.blockSize - 1, this.maxLengthValue)
      this.render()
      this.addNucleotideColors()
    } else {
      alert(`Position must be between 1 and ${this.maxLengthValue}`)
      inputField.value = this.currentStart
    }
  }

  changeBlockSize(event) {
    this.blockSize = parseInt(event.target.value)
    this.currentEnd = Math.min(this.currentStart + this.blockSize - 1, this.maxLengthValue)
    this.render()
    this.addNucleotideColors()
  }

  // Utility method to escape HTML
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Export as FASTA
  exportFasta() {
    const start = this.currentStart
    const end = this.currentEnd

    let fasta = ""
    this.sequencesValue.forEach((sequence, index) => {
      const header = this.headersValue[index] || `Sequence_${index + 1}`
      const subsequence = sequence.substring(start - 1, end)
      fasta += `>${header} [${start}-${end}]\n${subsequence}\n`
    })

    // Download as file
    const blob = new Blob([fasta], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `alignment_${start}-${end}.fasta`
    a.click()
    URL.revokeObjectURL(url)
  }
}
